import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { AuthService, PredefinedUserZone } from "@saliente/common-auth";
import { dateToInt, isEmailValid } from "@saliente/library";
import { round } from "lodash-es";
import { ToastrService } from "ngx-toastr";
import { take } from "rxjs/operators";
import { CaenCodeModel, DuActivitatiIndependente2025Model, DuChiriiModel, DuPfa2025Model, FormStatus, SistemImpozitareActivitatiIndependente, SistemImpozitareChirii } from "../client-forms.models";
import { ClientFormsService } from "../client-forms.service";

@Component({
    selector: 'du-pfa-2025',
    templateUrl: 'du-pfa-2025.component.html',
    styleUrls: ['du-pfa-2025.component.scss'],
    standalone: false
})
export class DuPFA2025Component implements OnInit, OnDestroy, OnChanges {
    @Input() public isDialog: boolean;
    @Input() public isPfa: boolean;
    @Input() public model: DuPfa2025Model;

    @Output() public save = new EventEmitter();
	@Output() public close = new EventEmitter();
	@Output() public invalidate = new EventEmitter();
    @Output() public downloadXml = new EventEmitter();


    // view child
	@ViewChild('duPfaForm') public duPfaForm: NgForm;

    //booleans
    public isReadOnly: boolean;
    public isExpert: boolean;
    public showStatutInfo = false;

    //models
    public caenCodes: CaenCodeModel[];
    public statutDictionary: any[] = [
        { value: 1, text: 'Nu datorez contribuții sociale în România' },
        { value: 2, text: 'Pensionar' },
        { value: 3, text: 'Persoană cu venituri din salarii și asimilate salariilor în anul 2025 sub 6 salarii minime brute' },
        { value: 4, text: 'Persoană cu venituri din salarii și asimilate salariilor în anul 2025 cel puțin egale cu 6 salarii minime brute pe țară' },
        { value: 5, text: 'Asigurat în sistem propriu de asigurări sociale (ex. avocat, notar)' },
        { value: 6, text: 'Elevi/studenți sau tineri cu vârste de până la 26 de ani care provin din sistemul de protecție a copilului' },
    ];

    public notEditableFormReason: string = "Declarația nu se poate completa, contactați expertul dumneavoastră în Connect.";

    constructor(private clientFormsService: ClientFormsService, private authService: AuthService, private toastr: ToastrService) {
        this.clientFormsService.getCaenCodeList().pipe(take(1))
            .subscribe((codes: CaenCodeModel[]) => {
                this.caenCodes = codes;
            })
    }

    ngOnInit(): void {
        this.isReadOnly = this.model.formData.status == FormStatus.Submitted || this.isFormExpired;
        this.isExpert = this.authService.selectedZoneCode == PredefinedUserZone.Expert && this.authService.isInRole('EXPERT');
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!changes.model.firstChange) {
            this.model = changes.model.currentValue;
            this.isReadOnly = this.model.formData.status == FormStatus.Submitted || this.isFormExpired;
            this.isExpert = this.authService.selectedZoneCode == PredefinedUserZone.Expert && this.authService.isInRole('EXPERT');
        }
    }

    ngOnDestroy(): void {}

    get isFormExpired(): boolean {
        if (!this.model?.formData?.expires) return false;
        return dateToInt(new Date()) > this.model.formData.expires;
    }

    public get isFormEditable() {
        if (this.isPfa) {
            return true;
        }
        else if (!this.model.data.persoana.areHandicap && !this.model.data.persoana.nerezident) {
            return true;
        }
        return false;
    }

    public onAreHandicapChanged(event: any) {
        if (!this.isPfa && this.model.data.persoana.areHandicap) {
            this.saveForm(this.duPfaForm);
        }
    }

    public onNerezidentChanged(event: any) {
        if (!this.isPfa && this.model.data.persoana.nerezident) {
            this.saveForm(this.duPfaForm);
        }
    }

    public addNewActivitateRealizat() {
        if (this.model.data.activitatiIndependenteRealizat == undefined) {
            this.model.data.activitatiIndependenteRealizat = [];
        }

        let newActivitateRealizat = new DuActivitatiIndependente2025Model();
        this.model.data.activitatiIndependenteRealizat = [...this.model.data.activitatiIndependenteRealizat, newActivitateRealizat];
    }

    public deleteActivitateRealizat(activitateRealizatIndex: number) {
        this.model.data.activitatiIndependenteRealizat.splice(activitateRealizatIndex, 1);
    }

    public addNewChirieRealizat() {
        if (this.model.data.chiriiRealizat == undefined) {
            this.model.data.chiriiRealizat = [];
        }

        let newChirieRealizat = new DuChiriiModel();
        newChirieRealizat.sistemImpozitare = SistemImpozitareChirii.CoteForfetare;
        this.model.data.chiriiRealizat = [...this.model.data.chiriiRealizat, newChirieRealizat];
    }

    public deleteChirieRealizat(chirieRealizatIndex: number) {
        this.model.data.chiriiRealizat.splice(chirieRealizatIndex, 1);
    }

    public dividendeRealizatOnBlur() {
        if(this.model.data.dividendeRealizat) {
            this.model.data.dividendeRealizat = round(this.model.data.dividendeRealizat);
        }
    }

    public personHelp() {
		window.open("https://app.keez.ro/help/client/antreprenor/declaratia_unica/DU_PFA/DU_dateContribuabil.html", '_blank', 'noopener noreferrer');
    }

    public activitatiIndependenteHelp() {
        return "https://app.keez.ro/help/client/antreprenor/declaratia_unica/DU_PFA/DU_venituriActivitatiIndependente.html";
    }

    public chiriiHelp() {
		return "https://app.keez.ro/help/client/antreprenor/declaratia_unica/DU_PFA/DU_chirii.html";
    }

    public dividendeHelp() {
       return "https://app.keez.ro/help/client/antreprenor/declaratia_unica/DU_PFA/DU_dividende.html";
    }
    
    public venituriStrainatateHelp() {
        return "https://app.keez.ro/help/client/antreprenor/declaratia_unica/DU_PFA/DU_venituriStrainatate.html";
    }

    public cimNedeterminatHelp() {
        return "https://app.keez.ro/help/client/antreprenor/declaratia_unica/DU_PFA/DU_esteAngajat.html";
    }

    public comentariiHelp() {
        return "https://app.keez.ro/help/client/antreprenor/declaratia_unica/DU_PFA/DU_comentarii.html";
    }

    public validateEmail(value: string): string | boolean {
		if (value && value.trim() != "") {
			if (!isEmailValid(value.trim())) {
				return "Adresa de e-mail introdusă nu este validă.";
			}
		}

		return true;
	}

	public emailOnBlur(value: string) {
		if (value && value.trim() != "") {
			if (!isEmailValid(value.trim())) {
				this.toastr.error("Adresa de e-mail introdusă nu este validă. Vă rugăm să verificați dacă ați scris corect adresa și să eliminați eventualele spații sau caractere nepermise. Asigurați-vă că adresa de e-mail conține un singur simbol '@' și este formată corect, de exemplu, 'nume@exemplu.com'. Vă mulțumim!");
			}
		}
	}

    public deleteNormaDeVenitFromRealizat() {
        if (this.model.data.activitatiIndependenteRealizat != undefined) {
            this.model.data.activitatiIndependenteRealizat = this.model.data.activitatiIndependenteRealizat
                .filter(aiRealizat => aiRealizat.sistemImpozitare !== SistemImpozitareActivitatiIndependente.NormaDeVenit);
        }
    }

    public saveForm(duPfaForm: NgForm) {
        this.deleteNormaDeVenitFromRealizat();
        this.save.emit({duPfaForm: duPfaForm, doSubmit: false})
    }

    public submitForm(duPfaForm: NgForm) {
        this.save.emit({duPfaForm: duPfaForm, doSubmit: true})
    }

    public invalidateForm() {
        this.invalidate.emit();
    }

    public downloadFormXml() {
        this.downloadXml.emit();
    }
}